import { CheckInState, CheckInAction, DebugLog, Sale, SaleChild } from './types';
import { HybridSearchIndex } from './searchIndex';
import {
  INIT_LOADING_START,
  INIT_LOADING_SUCCESS,
  INIT_LOADING_FAILURE,
  SYNC_START,
  SYNC_SUCCESS,
  SYNC_FAILURE,
  SEARCH_TERM_CHANGE,
  SEARCH_RESULTS_UPDATE,
  CHECK_IN_START,
  CHECK_IN_SUCCESS,
  CHECK_IN_FAILURE,
  CHECK_IN_INDIVIDUAL_START,
  CHECK_IN_INDIVIDUAL_SUCCESS,
  CHECK_IN_INDIVIDUAL_FAILURE,
  DEBUG_LOG_ADD,
  ERROR_SET,
  ERROR_CLEAR
} from './actions';

const initialState: CheckInState = {
  // Data
  sales: {},
  entries: {},
  campaign: null,
  
  // Search
  searchTerm: '',
  searchResults: [],
  searchIndex: null,
  
  // UI State
  isLoading: false,
  isInitialLoading: false,
  isSyncing: false,
  lastSyncTime: null,
  
  // Stats
  stats: {
    totalSales: 0,
    totalEntries: 0,
    checkedInEntries: 0,
    checkedInSales: 0
  },
  
  // Errors and debugging
  errors: {},
  debugLogs: [],
  
  // Check-in operations
  pendingCheckIns: {}
};

// Helper function to normalize sales into state structure
const normalizeSales = (sales: Sale[]) => {
  const salesMap: { [id: string]: Sale } = {};
  const entriesMap: { [id: string]: SaleChild & { saleId: string; index: number } } = {};

  sales.forEach(sale => {
    salesMap[sale.id.toString()] = sale;
    
    sale.details.forEach((entry, index) => {
      const entryId = `${sale.id}-${index}`;
      entriesMap[entryId] = {
        ...entry,
        saleId: sale.id.toString(),
        index
      };
    });
  });

  return { salesMap, entriesMap };
};

// Helper function to calculate stats
const calculateStats = (sales: { [id: string]: Sale }) => {
  let totalSales = 0;
  let totalEntries = 0;
  let checkedInEntries = 0;
  let checkedInSales = 0;

  Object.values(sales).forEach(sale => {
    totalSales++;
    if (sale.checkedIn) checkedInSales++;
    
    sale.details.forEach(entry => {
      totalEntries++;
      if (entry.checkedIn) checkedInEntries++;
    });
  });

  return {
    totalSales,
    totalEntries,
    checkedInEntries,
    checkedInSales
  };
};

// Helper function to add debug log with max limit
const addDebugLogToState = (logs: DebugLog[], newLog: DebugLog): DebugLog[] => {
  const updatedLogs = [...logs, newLog];
  // Keep only last 100 logs to prevent memory issues
  if (updatedLogs.length > 100) {
    return updatedLogs.slice(-100);
  }
  return updatedLogs;
};

const checkInReducer = (state = initialState, action: CheckInAction): CheckInState => {
  switch (action.type) {
    case INIT_LOADING_START:
      return {
        ...state,
        isInitialLoading: true,
        isLoading: true,
        errors: {}
      };

    case INIT_LOADING_SUCCESS: {
      const { campaign, sales } = action.payload;
      const { salesMap, entriesMap } = normalizeSales(sales);
      
      // Create search index with debug callback
      const searchIndex = new HybridSearchIndex((log: DebugLog) => {
        // This will be handled by the component to update the state
      });
      searchIndex.buildIndex(sales);

      return {
        ...state,
        isInitialLoading: false,
        isLoading: false,
        campaign,
        sales: salesMap,
        entries: entriesMap,
        searchIndex,
        stats: calculateStats(salesMap),
        lastSyncTime: Date.now(),
        errors: {}
      };
    }

    case INIT_LOADING_FAILURE:
      return {
        ...state,
        isInitialLoading: false,
        isLoading: false,
        errors: {
          ...state.errors,
          initialization: action.payload.error
        }
      };

    case SYNC_START:
      return {
        ...state,
        isSyncing: true
      };

    case SYNC_SUCCESS: {
      const { updatedSales } = action.payload;
      
      if (updatedSales.length === 0) {
        return {
          ...state,
          isSyncing: false,
          lastSyncTime: Date.now()
        };
      }

      const { salesMap: newSalesMap, entriesMap: newEntriesMap } = normalizeSales(updatedSales);
      
      // Merge updated sales into existing state
      const updatedSalesState = {
        ...state.sales,
        ...newSalesMap
      };

      // Update entries (remove old entries for updated sales, add new ones)
      const updatedEntriesState = { ...state.entries };
      updatedSales.forEach((sale: Sale) => {
        // Remove old entries for this sale
        Object.keys(updatedEntriesState).forEach(entryId => {
          if (entryId.startsWith(`${sale.id}-`)) {
            delete updatedEntriesState[entryId];
          }
        });
      });
      
      // Add new entries
      Object.assign(updatedEntriesState, newEntriesMap);

      // Update search index
      if (state.searchIndex) {
        state.searchIndex.updateSales(updatedSales);
      }

      return {
        ...state,
        isSyncing: false,
        sales: updatedSalesState,
        entries: updatedEntriesState,
        stats: calculateStats(updatedSalesState),
        lastSyncTime: Date.now()
      };
    }

    case SYNC_FAILURE:
      return {
        ...state,
        isSyncing: false,
        errors: {
          ...state.errors,
          sync: action.payload.error
        }
      };

    case SEARCH_TERM_CHANGE:
      return {
        ...state,
        searchTerm: action.payload.searchTerm
      };

    case SEARCH_RESULTS_UPDATE:
      return {
        ...state,
        searchResults: action.payload.results
      };

    case CHECK_IN_START: {
      const { saleId } = action.payload;
      
      return {
        ...state,
        pendingCheckIns: {
          ...state.pendingCheckIns,
          [saleId]: true
        }
      };
    }

    case CHECK_IN_SUCCESS: {
      const { saleId, updatedSale } = action.payload;
      const updatedSales = {
        ...state.sales,
        [saleId]: updatedSale
      };

      // Update entries for this sale
      const updatedEntries = { ...state.entries };
      
      // Remove old entries for this sale
      Object.keys(updatedEntries).forEach(entryId => {
        if (entryId.startsWith(`${saleId}-`)) {
          delete updatedEntries[entryId];
        }
      });

      // Add updated entries
      updatedSale.details.forEach((entry: SaleChild, index: number) => {
        const entryId = `${saleId}-${index}`;
        updatedEntries[entryId] = {
          ...entry,
          saleId: saleId.toString(),
          index
        };
      });

      // Update search index
      if (state.searchIndex) {
        state.searchIndex.updateSale(updatedSale);
      }

      const newPendingCheckIns = { ...state.pendingCheckIns };
      delete newPendingCheckIns[saleId];

      const newErrors = { ...state.errors };
      delete newErrors[`checkIn-${saleId}`];

      return {
        ...state,
        sales: updatedSales,
        entries: updatedEntries,
        stats: calculateStats(updatedSales),
        pendingCheckIns: newPendingCheckIns,
        errors: newErrors
      };
    }

    case CHECK_IN_FAILURE: {
      const { saleId, error } = action.payload;
      const newPendingCheckIns = { ...state.pendingCheckIns };
      delete newPendingCheckIns[saleId];

      return {
        ...state,
        pendingCheckIns: newPendingCheckIns,
        errors: {
          ...state.errors,
          [`checkIn-${saleId}`]: error
        }
      };
    }

    case CHECK_IN_INDIVIDUAL_START: {
      const { entryKey } = action.payload;
      
      return {
        ...state,
        pendingCheckIns: {
          ...state.pendingCheckIns,
          [entryKey]: true
        }
      };
    }

    case CHECK_IN_INDIVIDUAL_SUCCESS: {
      const { saleId, entryKey, updatedSale } = action.payload;
      const updatedSales = {
        ...state.sales,
        [saleId]: updatedSale
      };

      // Update entries for this sale
      const updatedEntries = { ...state.entries };
      
      // Remove old entries for this sale
      Object.keys(updatedEntries).forEach(entryId => {
        if (entryId.startsWith(`${saleId}-`)) {
          delete updatedEntries[entryId];
        }
      });

      // Add updated entries
      updatedSale.details.forEach((entry: SaleChild, index: number) => {
        const entryId = `${saleId}-${index}`;
        updatedEntries[entryId] = {
          ...entry,
          saleId: saleId.toString(),
          index
        };
      });

      // Update search index
      if (state.searchIndex) {
        state.searchIndex.updateSale(updatedSale);
      }

      const newPendingCheckIns = { ...state.pendingCheckIns };
      delete newPendingCheckIns[entryKey];

      const newErrors = { ...state.errors };
      delete newErrors[`checkInIndividual-${entryKey}`];

      return {
        ...state,
        sales: updatedSales,
        entries: updatedEntries,
        stats: calculateStats(updatedSales),
        pendingCheckIns: newPendingCheckIns,
        errors: newErrors
      };
    }

    case CHECK_IN_INDIVIDUAL_FAILURE: {
      const { entryKey, error } = action.payload;
      const newPendingCheckIns = { ...state.pendingCheckIns };
      delete newPendingCheckIns[entryKey];

      return {
        ...state,
        pendingCheckIns: newPendingCheckIns,
        errors: {
          ...state.errors,
          [`checkInIndividual-${entryKey}`]: error
        }
      };
    }

    case DEBUG_LOG_ADD:
      return {
        ...state,
        debugLogs: addDebugLogToState(state.debugLogs, action.payload.log)
      };

    case ERROR_SET:
      return {
        ...state,
        errors: {
          ...state.errors,
          [action.payload.key]: action.payload.message
        }
      };

    case ERROR_CLEAR: {
      const newErrors = { ...state.errors };
      delete newErrors[action.payload.key];
      return {
        ...state,
        errors: newErrors
      };
    }

    default:
      return state;
  }
};

export default checkInReducer;
